<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAdminMail } from '@/composables/useMail'
import { handleAxiosError } from '@/composables/useAxiosError'
import type { MessageLogEntry } from '@/types/dashboard/mail'

const { t } = useI18n()
const { getMessages } = useAdminMail()

const loading = ref(true)
const messages = ref<MessageLogEntry[]>([])
const total = ref(0)
const page = ref(1)
const limit = 50

const directionFilter = ref('')
const spamFilter = ref('')
const searchQuery = ref('')
const autoRefresh = ref(false)
let refreshInterval: ReturnType<typeof setInterval> | null = null

const offset = computed(() => (page.value - 1) * limit)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / limit)))

const fetchMessages = async () => {
  loading.value = true
  try {
    const res = await getMessages({
      limit,
      offset: offset.value,
      direction: directionFilter.value || undefined,
      spam_result: spamFilter.value || undefined,
      search: searchQuery.value || undefined
    })
    if (res.data) {
      messages.value = res.data.messages
      total.value = res.data.total
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AdminMailMessages' })
  } finally {
    loading.value = false
  }
}

const toggleAutoRefresh = () => {
  autoRefresh.value = !autoRefresh.value
  if (autoRefresh.value) {
    refreshInterval = setInterval(fetchMessages, 30000)
  } else if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

const applyFilters = () => {
  page.value = 1
  fetchMessages()
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

const formatTime = (ts: string): string => {
  const d = new Date(ts)
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const spamColor = (result: string): string => {
  switch (result) {
    case 'ham': return 'success'
    case 'spam': return 'error'
    case 'reject': return 'error'
    default: return 'grey'
  }
}

onMounted(() => fetchMessages())
onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
})
</script>

<template>
  <div class="admin-mail-messages">
    <div class="page-header">
      <h1>{{ t('dashboard.mail.admin.messages.title') }}</h1>
      <div class="header-actions">
        <button class="toggle-btn" :class="{ active: autoRefresh }" @click="toggleAutoRefresh">
          <v-icon :icon="autoRefresh ? 'mdi-pause' : 'mdi-play'" size="16" />
          <span>Auto</span>
        </button>
        <button class="refresh-btn" :disabled="loading" @click="fetchMessages">
          <v-icon icon="mdi-refresh" size="18" :class="{ spinning: loading }" />
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <div class="filter-group">
        <button class="filter-chip" :class="{ active: directionFilter === '' }" @click="directionFilter = ''; applyFilters()">
          {{ t('common.all') }}
        </button>
        <button class="filter-chip" :class="{ active: directionFilter === 'inbound' }" @click="directionFilter = 'inbound'; applyFilters()">
          <v-icon icon="mdi-arrow-down-bold" size="14" /> {{ t('dashboard.mail.admin.messages.inbound') }}
        </button>
        <button class="filter-chip" :class="{ active: directionFilter === 'outbound' }" @click="directionFilter = 'outbound'; applyFilters()">
          <v-icon icon="mdi-arrow-up-bold" size="14" /> {{ t('dashboard.mail.admin.messages.outbound') }}
        </button>
      </div>
      <div class="filter-group">
        <button class="filter-chip" :class="{ active: spamFilter === '' }" @click="spamFilter = ''; applyFilters()">
          {{ t('common.all') }}
        </button>
        <button class="filter-chip ham" :class="{ active: spamFilter === 'ham' }" @click="spamFilter = 'ham'; applyFilters()">
          Ham
        </button>
        <button class="filter-chip spam" :class="{ active: spamFilter === 'spam' }" @click="spamFilter = 'spam'; applyFilters()">
          Spam
        </button>
      </div>
      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        :placeholder="t('dashboard.mail.admin.messages.searchPlaceholder')"
        @keyup.enter="applyFilters"
      >
    </div>

    <!-- Loading -->
    <div v-if="loading && messages.length === 0" class="loading-state">
      <v-progress-circular indeterminate size="48" width="3" color="primary" />
    </div>

    <!-- Messages Table -->
    <div v-else-if="messages.length > 0" class="messages-table">
      <div class="table-header">
        <span class="col-time">{{ t('dashboard.mail.admin.messages.time') }}</span>
        <span class="col-dir"></span>
        <span class="col-sender">{{ t('dashboard.mail.admin.messages.sender') }}</span>
        <span class="col-recipient">{{ t('dashboard.mail.admin.messages.recipient') }}</span>
        <span class="col-subject">{{ t('dashboard.mail.admin.messages.subject') }}</span>
        <span class="col-size">{{ t('dashboard.mail.admin.messages.size') }}</span>
        <span class="col-spam">Spam</span>
      </div>
      <div v-for="msg in messages" :key="msg.id" class="table-row">
        <span class="col-time">{{ formatTime(msg.timestamp) }}</span>
        <span class="col-dir">
          <v-icon
            :icon="msg.direction === 'inbound' ? 'mdi-arrow-down-bold' : 'mdi-arrow-up-bold'"
            :color="msg.direction === 'inbound' ? '#64b5f6' : '#81c784'"
            size="18"
          />
        </span>
        <span class="col-sender" :title="msg.sender">{{ msg.sender }}</span>
        <span class="col-recipient" :title="msg.recipients.join(', ')">{{ msg.recipients[0] }}</span>
        <span class="col-subject" :title="msg.subject">{{ msg.subject || '—' }}</span>
        <span class="col-size">{{ formatSize(msg.size) }}</span>
        <span class="col-spam">
          <v-chip :color="spamColor(msg.spam_result)" size="x-small" variant="tonal">
            {{ msg.spam_result }}
          </v-chip>
        </span>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="empty-state">
      <v-icon icon="mdi-email-off-outline" size="48" />
      <p>{{ t('dashboard.mail.admin.messages.empty') }}</p>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button :disabled="page <= 1" @click="page--; fetchMessages()">
        <v-icon icon="mdi-chevron-left" size="18" />
      </button>
      <span class="page-info">{{ page }} / {{ totalPages }}</span>
      <button :disabled="page >= totalPages" @click="page++; fetchMessages()">
        <v-icon icon="mdi-chevron-right" size="18" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.admin-mail-messages {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.header-actions {
  display: flex;
  gap: 8px;
}

.refresh-btn, .toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) { background: rgba(120, 163, 255, 0.2); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.active { background: rgba(120, 163, 255, 0.25); border-color: rgba(120, 163, 255, 0.4); }
}

.spinning { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
}

.filter-group {
  display: flex;
  gap: 4px;
}

.filter-chip {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;

  &:hover { background: rgba(120, 163, 255, 0.1); }
  &.active {
    background: rgba(120, 163, 255, 0.15);
    border-color: rgba(120, 163, 255, 0.3);
    color: rgba(200, 218, 255, 0.95);
  }
}

.search-input {
  flex: 1;
  min-width: 200px;
  padding: 8px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.9);
  font-size: 0.85rem;
  outline: none;

  &::placeholder { color: rgba(200, 218, 255, 0.3); }
  &:focus { border-color: rgba(120, 163, 255, 0.3); }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px;
}

.messages-table {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.table-header, .table-row {
  display: grid;
  grid-template-columns: 100px 36px 1fr 1fr 2fr 80px 80px;
  gap: 8px;
  padding: 10px 16px;
  align-items: center;
}

.table-header {
  background: rgba(120, 163, 255, 0.05);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  border-top: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.8);

  &:hover { background: rgba(120, 163, 255, 0.03); }
}

.col-sender, .col-recipient, .col-subject {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-size {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.5);
}

.col-time {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.5);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 24px;
  color: rgba(200, 218, 255, 0.4);
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;

  button {
    padding: 6px 10px;
    background: rgba(120, 163, 255, 0.1);
    border: 1px solid rgba(120, 163, 255, 0.15);
    border-radius: 6px;
    color: rgba(200, 218, 255, 0.8);
    cursor: pointer;

    &:disabled { opacity: 0.3; cursor: not-allowed; }
    &:hover:not(:disabled) { background: rgba(120, 163, 255, 0.2); }
  }

  .page-info {
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.5);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .page-header h1 { color: rgba(15, 23, 42, 0.95); }
  .messages-table { background: rgba(255, 255, 255, 0.7); border-color: rgba(28, 110, 227, 0.1); }
  .table-row { color: rgba(15, 23, 42, 0.8); }
  .search-input { background: rgba(255, 255, 255, 0.7); color: rgba(15, 23, 42, 0.9); }
}
</style>
